import { z } from 'zod';

// Contratos do fio da aba Acesso ao aplicativo (U02). Tudo sai de
// `services/accounts/bankAccount/customerAppAccess/types.ts`, que já documenta o contrato conferido
// contra o `CustomerAppAccessController.cs` (`customerAppAccess.ts:1-26`). Os enums viajam como texto
// porque a BankingAPI registra `JsonStringEnumConverter` (`types.ts:1-5`).

const text = z.string().nullish();

/* eslint-disable lingui/no-unlocalized-strings -- valores técnicos do fio */
/** `CustomerAppAccessStatus` (`types.ts:7`). */
export const APP_ACCESS_STATUSES = ['Pending', 'Active', 'Suspended', 'Revoked'] as const;
/** `ActivationChannelType` (`types.ts:9`). */
export const ACTIVATION_CHANNELS = ['Email', 'Phone'] as const;
/* eslint-enable lingui/no-unlocalized-strings */
export type AppAccessStatus = (typeof APP_ACCESS_STATUSES)[number];
export type ActivationChannel = (typeof ACTIVATION_CHANNELS)[number];

/**
 * `BankAccountHolder` (`types.ts:63-67`). O `personId` tem de vir daqui: o id da pessoa na CreditAPI
 * é outro registro e todo endpoint de acesso responde "pessoa não encontrada" com ele
 * (`customerAppAccess.ts:16-23`, `AppAccessTabContainer.tsx:66-67`).
 */
export const bankAccountHolderSchema = z.looseObject({
  personId: z.string(),
  personName: text,
  registrationNumber: text,
});
export type BankAccountHolder = z.infer<typeof bankAccountHolderSchema>;

/** `CustomerAppAccessReadModel` (`types.ts:11-30`): `grantedBy` é o único campo opcional do tipo. */
export const appAccessSchema = z.looseObject({
  id: z.string(),
  personId: text,
  personName: text,
  status: text,
  statusDisplay: text,
  identityProvider: text,
  /** Nulo enquanto o cliente não fez o primeiro login; é estado normal (`types.ts:18`). */
  externalId: text,
  activationChannelType: text,
  activationChannelTypeDisplay: text,
  activationChannelValue: text,
  grantedAt: text,
  activatedAt: text,
  lastLoginAt: text,
  revokedAt: text,
  revokeReason: text,
  grantedBy: text,
});
export type AppAccess = z.infer<typeof appAccessSchema>;

/**
 * `CustomerAppAccessCandidateAccount` (`types.ts:32-48`). `bankCode`, `agency` e `accountNumber` vêm
 * como número; `customerAppAccountAccessId` é o id da linha de junção, e é ele que revoga a conta —
 * não o `id` da conta (`types.ts:46-47`).
 */
export const candidateAccountSchema = z.looseObject({
  id: z.string(),
  bankCode: z.union([z.number(), z.string()]).nullish(),
  bankName: text,
  agency: z.union([z.number(), z.string()]).nullish(),
  accountNumber: z.union([z.number(), z.string()]).nullish(),
  type: text,
  typeDisplay: text,
  status: text,
  statusDisplay: text,
  personId: text,
  personName: text,
  alreadyGranted: z.boolean(),
  customerAppAccountAccessId: text,
});
export type CandidateAccount = z.infer<typeof candidateAccountSchema>;

/** `GrantableAccounts` devolve **array direto**, sem envelope e sem paginação (`customerAppAccess.ts:107`). */
export const candidateAccountListSchema = z.array(candidateAccountSchema);

/** `GrantCustomerAppAccessProps` (`types.ts:50-53`). */
export interface GrantAppAccessBody {
  readonly personId: string;
  readonly activationChannelType: ActivationChannel;
}

/** `CustomerAppAccessReasonProps` (`types.ts:55-57`). */
export interface AppAccessReasonBody {
  readonly reason: string;
}
